import { useEffect } from 'react';

/**
 * Custom hook to warn users about unsaved changes when they try to:
 * 1. Close or refresh the browser tab/window
 *
 * Note: For in-app navigation blocking, we handle it manually with window.confirm
 * since useBlocker requires createBrowserRouter (data router API).
 *
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {string} message - Custom message (mostly for documentation, browsers show generic messages)
 */
export function useUnsavedChanges(hasUnsavedChanges, message = 'You have unsaved changes. Are you sure you want to leave?') {
  // Handle browser close/refresh with beforeunload event
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        // Prevent the default behavior
        e.preventDefault();
        // Chrome requires returnValue to be set
        // Most modern browsers ignore custom messages and show a generic one
        e.returnValue = message;
        return message;
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges, message]);

  return { hasUnsavedChanges };
}

export default useUnsavedChanges;
